import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
    Gift, TrendingUp, Users as UsersIcon, CheckCircle2, Clock, Ban,
    Sparkles, Trophy, Activity, Loader2,
} from 'lucide-react';

/**
 * Admin → Referrals analytics page (Round 32).
 *
 * Single fetch to /api/admin/referrals returns:
 *   headline.{total_invites, qualified, pending, rejected, total_credits,
 *             conversion_rate, time_to_qualify}
 *   leaderboard[]      — top referrers, sorted by qualified desc
 *   rejection_breakdown[]
 *   trend_30d[]        — daily invites + qualified
 *   recent[]           — last 50 referrals
 */
export default function AdminReferrals() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get('/admin/referrals')
            .then((r) => setData(r.data))
            .finally(() => setLoading(false));
    }, []);

    if (loading) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center" data-testid="admin-referrals-loading">
                <Loader2 className="animate-spin text-marigold" size={24} />
            </div>
        );
    }
    if (!data) return null;

    const { headline, leaderboard, rejection_breakdown, trend_30d, recent } = data;
    const ttq = headline.time_to_qualify;

    return (
        <div className="p-6 lg:p-10 space-y-8" data-testid="admin-referrals-page">
            <header>
                <div className="overline">Referrals</div>
                <h1 className="font-serif text-3xl text-parchment mt-2 flex items-center gap-3">
                    <Gift size={28} className="text-marigold" /> Referral funnel
                </h1>
            </header>

            {/* Headline metrics */}
            <section className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="ref-headline">
                <Metric icon={UsersIcon} label="Total invites" value={headline.total_invites} testId="metric-invites" />
                <Metric icon={CheckCircle2} label="Qualified" value={headline.qualified} accent testId="metric-qualified" />
                <Metric icon={Clock} label="Pending" value={headline.pending} testId="metric-pending" />
                <Metric icon={Ban} label="Rejected" value={headline.rejected} testId="metric-rejected" />
                <Metric icon={Sparkles} label="Wallet ₹ issued" value={`₹${Number(headline.total_inr || 0).toLocaleString('en-IN')}`} accent testId="metric-inr" />
                <Metric
                    icon={TrendingUp}
                    label="Conversion rate"
                    value={`${headline.conversion_rate}%`}
                    sublabel="qualified / (qualified + pending)"
                    testId="metric-conversion"
                />
                <Metric
                    icon={Activity}
                    label="Avg time to qualify"
                    value={ttq.avg_hours != null ? `${ttq.avg_hours}h` : '—'}
                    sublabel={ttq.min_hours != null ? `min ${ttq.min_hours}h · max ${ttq.max_hours}h` : 'none yet'}
                    testId="metric-ttq"
                />
                <Metric
                    icon={Trophy}
                    label="Active referrers"
                    value={leaderboard.length}
                    sublabel="users with ≥1 invite"
                    testId="metric-active-refs"
                />
            </section>

            {/* Leaderboard */}
            <section data-testid="ref-leaderboard-section">
                <h2 className="font-serif text-xl text-parchment mb-4 flex items-center gap-2">
                    <Trophy size={18} className="text-marigold" /> Top referrers
                </h2>
                <Card className="rounded-sm bg-elevated border-marigold/10 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="text-left text-slate-500 text-xs uppercase tracking-wider border-b border-marigold/10">
                                <tr>
                                    <th className="px-4 py-3">#</th>
                                    <th className="px-4 py-3">Referrer</th>
                                    <th className="px-4 py-3">Code</th>
                                    <th className="px-4 py-3">Invited</th>
                                    <th className="px-4 py-3">Qualified</th>
                                    <th className="px-4 py-3">Pending</th>
                                    <th className="px-4 py-3">Rejected</th>
                                    <th className="px-4 py-3">Wallet ₹ earned</th>
                                    <th className="px-4 py-3">Last invite</th>
                                </tr>
                            </thead>
                            <tbody>
                                {leaderboard.length === 0 && (
                                    <tr><td colSpan="9" className="px-4 py-6 text-slate-500" data-testid="ref-leaderboard-empty">
                                        No referrers yet.
                                    </td></tr>
                                )}
                                {leaderboard.map((r, i) => (
                                    <tr key={r.referrer_id} className="border-b border-marigold/5 hover:bg-obsidian/40" data-testid={`ref-leader-${i}`}>
                                        <td className="px-4 py-3 text-slate-500">{i + 1}</td>
                                        <td className="px-4 py-3">
                                            <div className="text-parchment">{r.name || '—'}</div>
                                            <div className="text-xs text-slate-500">{r.email}</div>
                                        </td>
                                        <td className="px-4 py-3"><code className="text-marigold text-xs">{r.code}</code></td>
                                        <td className="px-4 py-3 text-slate-300">{r.invited}</td>
                                        <td className="px-4 py-3 text-emerald-400 font-medium">{r.qualified}</td>
                                        <td className="px-4 py-3 text-slate-300">{r.pending}</td>
                                        <td className="px-4 py-3 text-slate-500">{r.rejected}</td>
                                        <td className="px-4 py-3 text-marigold">+₹{Number(r.earned_inr || 0).toLocaleString('en-IN')}</td>
                                        <td className="px-4 py-3 text-slate-500 text-xs">{r.last_invite_at?.slice(0, 10) || '—'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Card>
            </section>

            <div className="grid lg:grid-cols-2 gap-6">
                {/* 30-day trend */}
                <section data-testid="ref-trend-section">
                    <h2 className="font-serif text-xl text-parchment mb-4">30-day signup trend</h2>
                    <TrendChart data={trend_30d} />
                </section>

                {/* Rejection breakdown */}
                <section data-testid="ref-rejection-section">
                    <h2 className="font-serif text-xl text-parchment mb-4">Rejection breakdown</h2>
                    <Card className="rounded-sm bg-elevated border-marigold/10 p-5">
                        {rejection_breakdown.length === 0 ? (
                            <p className="text-slate-500 text-sm" data-testid="ref-rejections-empty">
                                No rejections — clean funnel.
                            </p>
                        ) : (
                            <ul className="space-y-2" data-testid="ref-rejections-list">
                                {rejection_breakdown.map((r) => (
                                    <li key={r.reason} className="flex items-center justify-between text-sm" data-testid={`ref-reject-${r.reason}`}>
                                        <span className="text-slate-300">{prettyReason(r.reason)}</span>
                                        <Badge className="bg-rose-500/10 text-rose-400 border-rose-500/20">{r.count}</Badge>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </Card>
                </section>
            </div>

            {/* Recent activity */}
            <section data-testid="ref-recent-section">
                <h2 className="font-serif text-xl text-parchment mb-4">Recent activity</h2>
                <Card className="rounded-sm bg-elevated border-marigold/10 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="text-left text-slate-500 text-xs uppercase tracking-wider border-b border-marigold/10">
                                <tr>
                                    <th className="px-4 py-3">Referee</th>
                                    <th className="px-4 py-3">Via code</th>
                                    <th className="px-4 py-3">Status</th>
                                    <th className="px-4 py-3">Signed up</th>
                                    <th className="px-4 py-3">Qualified at</th>
                                    <th className="px-4 py-3">Reward</th>
                                </tr>
                            </thead>
                            <tbody>
                                {recent.length === 0 && (
                                    <tr><td colSpan="6" className="px-4 py-6 text-slate-500" data-testid="ref-recent-empty">
                                        No referrals yet.
                                    </td></tr>
                                )}
                                {recent.map((r, i) => (
                                    <tr key={i} className="border-b border-marigold/5" data-testid={`ref-recent-${i}`}>
                                        <td className="px-4 py-3">
                                            <div className="text-parchment">{r.referee_name || '—'}</div>
                                            <div className="text-xs text-slate-500">{r.referee_email}</div>
                                        </td>
                                        <td className="px-4 py-3"><code className="text-marigold text-xs">{r.referrer_code}</code></td>
                                        <td className="px-4 py-3">
                                            <StatusBadge status={r.status} reason={r.rejection_reason} />
                                        </td>
                                        <td className="px-4 py-3 text-slate-500 text-xs">{r.signup_at?.slice(0, 10) || '—'}</td>
                                        <td className="px-4 py-3 text-slate-500 text-xs">{r.qualified_at?.slice(0, 10) || '—'}</td>
                                        <td className="px-4 py-3 text-marigold">{
                                            Number(r.reward_inr || 0) > 0
                                                ? (r.reward_currency === 'USD'
                                                    ? `+$${Number(r.reward_inr).toFixed(2)}`
                                                    : `+₹${Number(r.reward_inr).toLocaleString('en-IN')}`)
                                                : '—'
                                        }</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Card>
            </section>
        </div>
    );
}


function Metric({ icon: Icon, label, value, sublabel, accent, testId }) {
    return (
        <Card
            className={`p-4 rounded-sm ${accent ? 'bg-marigold/5 border-marigold/30' : 'bg-elevated border-marigold/10'}`}
            data-testid={testId}
        >
            <div className="flex items-center gap-2 text-slate-400 mb-1">
                <Icon size={14} />
                <span className="text-xs uppercase tracking-wider">{label}</span>
            </div>
            <div className={`text-2xl font-serif ${accent ? 'text-marigold' : 'text-parchment'}`}>
                {value}
            </div>
            {sublabel && <div className="text-xs text-slate-500 mt-1">{sublabel}</div>}
        </Card>
    );
}


function StatusBadge({ status, reason }) {
    if (status === 'qualified') {
        return <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20">qualified</Badge>;
    }
    if (status === 'pending') {
        return <Badge className="bg-marigold/10 text-marigold border-marigold/30">pending</Badge>;
    }
    return (
        <Badge className="bg-slate-500/10 text-slate-400 border-slate-500/20" title={reason || ''}>
            rejected
        </Badge>
    );
}


function prettyReason(reason) {
    const map = {
        unknown_code:              'Unknown / invalid code',
        self_referral:             'Self-referral',
        self_referral_email_match: 'Self-referral (email match)',
        duplicate_referee:         'Duplicate referee email',
    };
    return map[reason] || reason;
}


function TrendChart({ data }) {
    if (!data || data.length === 0) {
        return (
            <Card className="rounded-sm bg-elevated border-marigold/10 p-8" data-testid="ref-trend-empty">
                <p className="text-slate-500 text-sm">No referral signups in the last 30 days.</p>
            </Card>
        );
    }
    // Inline SVG bar chart — keeps zero deps.
    const width = 600;
    const height = 160;
    const pad = 24;
    const max = Math.max(...data.map(d => d.invites), 1);
    const barW = (width - pad * 2) / data.length;

    return (
        <Card className="rounded-sm bg-elevated border-marigold/10 p-5" data-testid="ref-trend-chart">
            <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-40">
                {data.map((d, i) => {
                    const x = pad + i * barW;
                    const totalH = (d.invites / max) * (height - pad * 2);
                    const qH = (d.qualified / max) * (height - pad * 2);
                    return (
                        <g key={d.date}>
                            <rect
                                x={x + 2} y={height - pad - totalH}
                                width={barW - 4} height={totalH}
                                fill="rgba(245, 158, 11, 0.25)"
                            />
                            <rect
                                x={x + 2} y={height - pad - qH}
                                width={barW - 4} height={qH}
                                fill="rgba(245, 158, 11, 0.9)"
                            />
                        </g>
                    );
                })}
                <line x1={pad} y1={height - pad} x2={width - pad} y2={height - pad}
                      stroke="rgba(245, 158, 11, 0.2)" />
            </svg>
            <div className="flex items-center justify-between text-xs text-slate-500 mt-2">
                <span>{data[0]?.date}</span>
                <span className="flex items-center gap-3">
                    <span className="flex items-center gap-1">
                        <span className="w-2 h-2 bg-marigold/30 inline-block" /> Invites
                    </span>
                    <span className="flex items-center gap-1">
                        <span className="w-2 h-2 bg-marigold inline-block" /> Qualified
                    </span>
                </span>
                <span>{data[data.length - 1]?.date}</span>
            </div>
        </Card>
    );
}
